// CreateSessionForm — the one form for scheduling a session; used wherever admins create sessions.
// Dropdown options come from useConfig / the live tutor list, never hardcoded. Validation reuses
// createSessionSchema, so the client and POST /api/sessions agree on what a valid session is.
import { useState } from "react";
import { createSessionSchema } from "@dm-lms/shared/validations";
import type { ConfigOption, PaginatedResult, Session } from "@dm-lms/shared/types";
import { postJson, useFetch } from "../../hooks/useApi.js";
import { useConfig } from "../../hooks/useConfig.js";
import type { ActiveTutor } from "../../lib/adminTutorTypes.js";

interface CreateSessionFormProps {
  courseId:        string;   // pre-selected course
  courseTutorId:   string;   // default tutor — sessions inherit it unless another is chosen
  courseTutorName: string;
  onSuccess:       (session: Session) => void;
  onCancel:        () => void;
}

const INPUT_CLASS =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal";
const LABEL_CLASS = "block text-sm font-medium text-dm-navy";

// Sentinel <select> value for "type a venue not in the config list" — can't collide with a config key.
const CUSTOM_VENUE = "__custom__";

// The only place that decides which location fields a delivery mode needs.
function fieldsForMode(mode: string): { venue: boolean; meetingUrl: boolean } {
  return {
    venue:      mode === "physical" || mode === "hybrid",
    meetingUrl: mode === "virtual" || mode === "hybrid",
  };
}

function todayLocalDate(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function ConfigSelect({
  id, value, onChange, options, loading, placeholder, extra,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: ConfigOption[];
  loading: boolean;
  placeholder: string;
  extra?: { value: string; label: string };
}) {
  return (
    <select
      id={id}
      value={value}
      disabled={loading}
      onChange={(event) => { onChange(event.target.value); }}
      className={INPUT_CLASS}
    >
      <option value="">{loading ? "Loading…" : placeholder}</option>
      {options.map((option) => (
        <option key={option.key} value={option.key}>{option.label}</option>
      ))}
      {extra && <option value={extra.value}>{extra.label}</option>}
    </select>
  );
}

export default function CreateSessionForm({
  courseId, courseTutorId, courseTutorName, onSuccess, onCancel,
}: CreateSessionFormProps) {
  const modes = useConfig("course_mode");
  const durations = useConfig("session_duration");
  const venues = useConfig("session_venue");
  const { data: tutorsData } = useFetch<PaginatedResult<ActiveTutor>>("/api/admin/tutors/active?page=1&pageSize=100");

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [mode, setMode] = useState("");
  const [durationKey, setDurationKey] = useState("");
  const [tutorId, setTutorId] = useState(courseTutorId);
  const [venueChoice, setVenueChoice] = useState("");
  const [customVenue, setCustomVenue] = useState("");
  const [meetingUrl, setMeetingUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const show = fieldsForMode(mode);
  const otherTutors = (tutorsData?.items ?? []).filter((tutor) => tutor.userId !== courseTutorId);
  const canSubmit = title.trim() !== "" && date !== "" && time !== "" && mode !== "" && durationKey !== "";

  function resolvedVenue(): string | null {
    if (!show.venue) return null;
    const text = venueChoice === CUSTOM_VENUE
      ? customVenue.trim()
      : venues.options.find((option) => option.key === venueChoice)?.label ?? "";
    return text || null;
  }

  function reset() {
    setTitle(""); setDate(""); setTime(""); setMode(""); setDurationKey("");
    setTutorId(courseTutorId); setVenueChoice(""); setCustomVenue(""); setMeetingUrl("");
    setError(null);
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const scheduled = new Date(`${date}T${time}`);
    if (Number.isNaN(scheduled.getTime())) {
      setError("Enter a valid date and time");
      return;
    }

    const parsed = createSessionSchema.safeParse({
      courseId,
      title:           title.trim(),
      scheduledAt:     scheduled.toISOString(),
      durationMinutes: parseInt(durationKey, 10),
      mode,
      meetingUrl:      show.meetingUrl && meetingUrl.trim() ? meetingUrl.trim() : null,
      venue:           resolvedVenue(),
      // Only an override is sent — omitted, the API inherits the course's tutor.
      tutorId:         tutorId !== courseTutorId ? tutorId : undefined,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Please check the form");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await postJson<Session>("/api/sessions", parsed.data);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      reset();
      onSuccess(res.data);
    } catch {
      setError("Unable to create session. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={(event) => { void handleSubmit(event); }}
      className="space-y-4 rounded-lg border border-gray-200 bg-white p-4"
    >
      <div>
        <label htmlFor="session-title" className={LABEL_CLASS}>Title</label>
        <input
          id="session-title"
          type="text"
          required
          minLength={3}
          maxLength={200}
          value={title}
          onChange={(event) => { setTitle(event.target.value); }}
          className={INPUT_CLASS}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="session-date" className={LABEL_CLASS}>Date</label>
          <input
            id="session-date"
            type="date"
            required
            min={todayLocalDate()}
            value={date}
            onChange={(event) => { setDate(event.target.value); }}
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label htmlFor="session-time" className={LABEL_CLASS}>Time</label>
          <input
            id="session-time"
            type="time"
            required
            value={time}
            onChange={(event) => { setTime(event.target.value); }}
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label htmlFor="session-mode" className={LABEL_CLASS}>Mode</label>
          <ConfigSelect
            id="session-mode"
            value={mode}
            onChange={setMode}
            options={modes.options}
            loading={modes.loading}
            placeholder="Select mode"
          />
        </div>
        <div>
          <label htmlFor="session-duration" className={LABEL_CLASS}>Duration</label>
          <ConfigSelect
            id="session-duration"
            value={durationKey}
            onChange={setDurationKey}
            options={durations.options}
            loading={durations.loading}
            placeholder="Select duration"
          />
        </div>
      </div>

      <div>
        <label htmlFor="session-tutor" className={LABEL_CLASS}>Session tutor — defaults to course tutor</label>
        <select
          id="session-tutor"
          value={tutorId}
          onChange={(event) => { setTutorId(event.target.value); }}
          className={INPUT_CLASS}
        >
          <option value={courseTutorId}>{courseTutorName} (primary)</option>
          {otherTutors.map((tutor) => (
            <option key={tutor.userId} value={tutor.userId}>{tutor.name}</option>
          ))}
        </select>
      </div>

      {show.venue && (
        <div>
          <label htmlFor="session-venue" className={LABEL_CLASS}>Venue</label>
          <ConfigSelect
            id="session-venue"
            value={venueChoice}
            onChange={setVenueChoice}
            options={venues.options}
            loading={venues.loading}
            placeholder="Select venue"
            extra={{ value: CUSTOM_VENUE, label: "Other (type a venue)…" }}
          />
          {venueChoice === CUSTOM_VENUE && (
            <input
              type="text"
              aria-label="Custom venue"
              maxLength={500}
              value={customVenue}
              onChange={(event) => { setCustomVenue(event.target.value); }}
              className={INPUT_CLASS}
            />
          )}
        </div>
      )}

      {show.meetingUrl && (
        <div>
          <label htmlFor="session-meeting-url" className={LABEL_CLASS}>Meeting URL</label>
          <input
            id="session-meeting-url"
            type="url"
            placeholder="https://meet.google.com/..."
            value={meetingUrl}
            onChange={(event) => { setMeetingUrl(event.target.value); }}
            className={INPUT_CLASS}
          />
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={isSubmitting || !canSubmit}
          className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? "Creating…" : "Create session"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm text-dm-navy"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
